import React, { useMemo } from 'react';
import moment from 'moment';
import WindowedSelect from 'react-windowed-select';
import { fmt3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { parseApiDate, selectStyles } from '../leaseGeneralInvoiceReceipt/formUtils';

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '15px' };
const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });

const fmtDate = (value) => {
    const d = parseApiDate(value);
    return d ? moment(d).format('DD-MMM-YYYY') : '---';
};

const ReadOnly = ({ label, value }) => (
    <div>
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold">{value || '---'}</div>
    </div>
);

/**
 * Middle panel: pick property / unit / lease / tenant / payment type, then Add.
 * alloc: { propertyNo, unitNo, leaseNo, tenantNo, paymentTypeDesc, amount, showAll }
 */
const AllocationPanel = ({
    alloc, details, balanceAmount, disabled, warning, loading,
    properties, units, leases, tenants, paymentTypes,
    onProperty, onUnit, onLease, onTenant, onPaymentType, onAmount, onShowAll, onAdd, onReset,
}) => {
    const propertyOptions = useMemo(() => properties.map((p) => ({ value: p.value, label: p.text })), [properties]);
    const unitOptions = useMemo(() => units
        .slice()
        .sort((a, b) => `${a.unitNo}`.localeCompare(`${b.unitNo}`, undefined, { numeric: true, sensitivity: 'base' }))
        .map((u) => ({ value: u.unitNo, label: u.unitNo, raw: u })), [units]);
    const leaseOptions = useMemo(() => leases.map((l) => ({ value: l.leaseNo, label: l.leaseNo })), [leases]);
    const tenantOptions = useMemo(() => tenants.map((t) => ({ value: t.value, label: `${t.value} - ${t.text}` })), [tenants]);
    const paymentOptions = useMemo(() => paymentTypes.map((p) => ({ value: p.text, label: p.text })), [paymentTypes]);
    const pick = (options, value) => (value ? options.find((o) => o.value === value) || { value, label: value } : null);

    const balanceColor = balanceAmount === 0 ? '#198754' : '#B7791F';

    return (
        <div className="border-box2 p-3">
            <div style={GRID}>
                <div>
                    <label className="form-label">Property *</label>
                    <WindowedSelect
                        className="p-0" theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" styles={selectStyles}
                        isDisabled={disabled} placeholder="--Property--"
                        value={pick(propertyOptions, alloc.propertyNo)}
                        onChange={(opt) => onProperty(opt?.value || '')}
                        options={propertyOptions}
                    />
                </div>
                <div>
                    <label className="form-label">Unit No *</label>
                    <WindowedSelect
                        className="p-0" theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" styles={selectStyles}
                        isDisabled={disabled || !alloc.propertyNo}
                        value={pick(unitOptions, alloc.unitNo)}
                        onChange={(opt) => onUnit(opt?.raw || null)}
                        options={unitOptions}
                    />
                </div>
                <div>
                    <label className="form-label">Lease No *</label>
                    <WindowedSelect
                        className="p-0" theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" styles={selectStyles}
                        isDisabled={disabled || !alloc.propertyNo} placeholder="--Select--"
                        value={pick(leaseOptions, alloc.leaseNo)}
                        onChange={(opt) => onLease(opt?.value || '')}
                        options={leaseOptions}
                    />
                    <div className="form-check mt-1">
                        <input
                            id="miscShowAll" type="checkbox" className="form-check-input"
                            checked={alloc.showAll} disabled={disabled}
                            onChange={(e) => onShowAll(e.target.checked)}
                        />
                        <label htmlFor="miscShowAll" className="form-check-label">Show all leases</label>
                    </div>
                </div>
                <div>
                    <label className="form-label">Tenant *</label>
                    <WindowedSelect
                        className="p-0" theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" styles={selectStyles}
                        isDisabled={disabled} placeholder="--Tenant Name--"
                        value={pick(tenantOptions, alloc.tenantNo)}
                        onChange={(opt) => onTenant(opt?.value || '')}
                        options={tenantOptions}
                    />
                </div>
            </div>

            <div className="mt-3" style={{ ...GRID, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }} aria-busy={loading}>
                <ReadOnly label="Tenant No" value={details?.tenantNo} />
                <ReadOnly label="Tenant Name" value={details?.tenantName} />
                <ReadOnly label="Contact No" value={details?.contactNo} />
                <ReadOnly label="Group" value={details?.groupId} />
                <ReadOnly label="MTC No" value={details?.mtcNo} />
                <ReadOnly label="Lease From" value={details ? fmtDate(details.leaseFrom) : ''} />
                <ReadOnly label="Lease To" value={details ? fmtDate(details.leaseTo) : ''} />
                <ReadOnly label="Status" value={details?.status} />
            </div>

            <hr />
            <div style={{ ...GRID, alignItems: 'end' }}>
                <div>
                    <label className="form-label">Payment Type *</label>
                    <WindowedSelect
                        className="p-0" theme={selectTheme} menuPortalTarget={document.body} menuPlacement="auto" styles={selectStyles}
                        isDisabled={disabled} placeholder="--Payment Type--"
                        value={pick(paymentOptions, alloc.paymentTypeDesc)}
                        onChange={(opt) => onPaymentType(opt?.value || '')}
                        options={paymentOptions}
                    />
                </div>
                <div>
                    <label className="form-label">Allocate Amount *</label>
                    <input
                        type="number" min="0" step="0.001" className="p-2 form-control"
                        value={alloc.amount} disabled={disabled}
                        onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                        onChange={(e) => onAmount(e.target.value)}
                        onBlur={(e) => { if (e.target.value !== '') onAmount(Number(e.target.value).toFixed(3)); }}
                    />
                </div>
                <div>
                    <div className="text-muted small">Balance Amount</div>
                    <div className="fw-bold fs-5" style={{ color: balanceColor }} aria-live="polite">{fmt3(balanceAmount)}</div>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="mainbtn" disabled={disabled} onClick={onAdd}><div>Add</div></button>
                    <button type="button" className="mainbtnRed" disabled={disabled} onClick={onReset}><div>Reset</div></button>
                </div>
            </div>
            {warning && <div className="text-warning-emphasis small mt-2" role="status">⚠ {warning}</div>}
        </div>
    );
};

export default AllocationPanel;
